import type { Contract, Milestone } from '../types';
import { agreementTimelines } from './agreementTimelines';
import { contractTitle } from './contractTitle';

export interface TimelineAgreementContext {
  contract: Contract;
  pending: boolean;
  fundingNeedOre: number;
  originalStage?: NonNullable<Milestone['stageReplacement']>['originalStage'];
}

interface Entry {
  milestone: Milestone;
  aliases: Set<string>;
  context: TimelineAgreementContext;
}

/** Build the current project view by applying signed amendments in order.
 * Identity comes from explicit source links, never descriptions or numbers.
 * Presentation order does not change the persisted execution dependencies. */
export function combinedAgreementTimeline(
  current: Contract,
  contracts: Contract[],
  stages: Milestone[]
) {
  const groups = agreementTimelines(current, contracts, stages);
  if (!groups) return null;
  const chain = groups.map(
    (group) =>
      contracts.find((item) => item.id === group.contract.id) ?? group.contract
  );
  const plan = (contractId: string) =>
    stages
      .filter((stage) => stage.contractId === contractId)
      .sort((a, b) => a.milestoneNumber - b.milestoneNumber);
  const sourceInfo = (stage: Milestone, contract: Contract) => ({
    contractId: contract.id,
    contractNumber: contract.contractNumber,
    milestoneId: stage.id,
    milestoneNumber: stage.milestoneNumber
  });
  const clean = (stage: Milestone): Milestone => ({
    ...stage,
    stageReplacement: undefined,
    continuedBy: undefined
  });
  let entries: Entry[] = plan(chain[0].id).map((stage) => ({
    milestone: clean(stage),
    aliases: new Set([stage.id]),
    context: {
      contract: chain[0],
      pending: false,
      fundingNeedOre: 0,
      originalStage: sourceInfo(stage, chain[0])
    }
  }));
  for (const [index, agreement] of chain.entries()) {
    if (!index || !agreement.stageVariation) continue;
    const group = groups[index];
    const targets = plan(agreement.id);
    const restoredSources = new Map(
      (agreement.stageVariation.restoredMilestones ?? []).map((link) => [
        link.milestoneId,
        link.sourceMilestoneId
      ])
    );
    const sourceIdFor = (stage: Milestone) =>
      restoredSources.get(stage.id) ?? stage.replacesMilestoneId;
    const selected = new Set(agreement.stageVariation.selectedMilestoneIds);
    const successors = new Map(
      targets
        .filter((stage) => stage.replacesMilestoneId)
        .map((stage) => [stage.replacesMilestoneId!, stage])
    );
    const sourceById = new Map<string, Entry>();
    entries.forEach((entry) =>
      entry.aliases.forEach((id) => sourceById.set(id, entry))
    );
    const ownTargets = targets.filter(
      (stage) =>
        !stage.replacesMilestoneId ||
        selected.has(stage.replacesMilestoneId) ||
        sourceById.get(stage.replacesMilestoneId)?.milestone.stageReplacement
          ?.kind === 'removed'
    );
    const changedSources = new Set(
      ownTargets.flatMap((stage) =>
        sourceIdFor(stage) ? [sourceIdFor(stage)!] : []
      )
    );
    const affected = entries
      .map((entry, position) =>
        [...entry.aliases].some(
          (id) => selected.has(id) || changedSources.has(id)
        )
          ? position
          : -1
      )
      .filter((position) => position >= 0);
    const lastAffected = affected.length
      ? Math.max(...affected)
      : entries.length - 1;
    let insertion = 0;
    const retained: Entry[] = [];
    entries.forEach((entry, position) => {
      const selectedId = [...entry.aliases].find(
        (id) => selected.has(id) || changedSources.has(id)
      );
      if (selectedId && changedSources.has(selectedId)) return;
      if (selectedId) {
        const removed: Entry = {
          ...entry,
          context: {
            ...entry.context,
            contract: agreement,
            pending: group.awaitingActivation,
            fundingNeedOre: 0
          },
          milestone: {
            ...entry.milestone,
            continuedBy: undefined,
            stageReplacement: {
              kind: 'removed',
              contractId: agreement.id,
              contractNumber: agreement.contractNumber,
              description: contractTitle(agreement),
              milestoneId: null,
              originalStage: entry.context.originalStage
            }
          }
        };
        retained.push(removed);
      } else {
        const successor = [...entry.aliases]
          .map((id) => successors.get(id))
          .find(Boolean);
        retained.push(
          successor && !entry.milestone.stageReplacement
            ? {
                ...entry,
                aliases: new Set([...entry.aliases, successor.id]),
                milestone: group.awaitingActivation
                  ? entry.milestone
                  : clean(successor)
              }
            : entry
        );
      }
      if (position <= lastAffected) insertion++;
    });
    const incoming: Entry[] = ownTargets.map((stage) => {
      const sourceId = sourceIdFor(stage);
      const source = sourceId ? sourceById.get(sourceId) : undefined;
      const projected =
        group.milestones.find((item) => item.id === stage.id) ?? stage;
      return {
        milestone: clean(projected),
        aliases: new Set([...(source?.aliases ?? []), stage.id]),
        context: {
          contract: agreement,
          pending: group.awaitingActivation,
          fundingNeedOre: Math.max(
            0,
            Number(group.contract.remainingAmount) * 100
          ),
          originalStage:
            source?.context.originalStage ?? sourceInfo(stage, agreement)
        }
      };
    });
    retained.splice(insertion, 0, ...incoming);
    entries = retained;
  }
  const last = chain[chain.length - 1];
  return {
    root: chain[0],
    contract: last,
    milestones: entries.map((entry) => entry.milestone),
    contexts: Object.fromEntries(
      entries.map((entry) => [entry.milestone.id, entry.context])
    ),
    history: chain.map((contract, index) => ({
      contract,
      milestones: [
        ...plan(contract.id).filter(
          (stage) =>
            !contract.stageVariation ||
            !stage.replacesMilestoneId ||
            contract.stageVariation.selectedMilestoneIds.includes(
              stage.replacesMilestoneId
            )
        ),
        ...groups[index].milestones.filter(
          (stage) =>
            stage.stageReplacement?.kind === 'removed' &&
            stage.stageReplacement.contractId === contract.id
        )
      ]
    })),
    pending: groups[groups.length - 1].awaitingActivation
  };
}
